import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Globe, ArrowUpRight } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Navbar = () => {
  const { t, currentLanguage, changeLanguage, languages, direction } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const location = useLocation();
  
  // Check if we're on the homepage
  const isHomePage = location.pathname === '/';
  
  // Navbar should be dark when: scrolled OR not on homepage
  const shouldBeDark = isScrolled || !isHomePage;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/products', label: t('nav.products') },
    { path: '/generators', label: t('nav.generators') },
    { path: '/markets', label: t('nav.markets') },
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const currentLang = languages.find(l => l.code === currentLanguage);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          shouldBeDark
            ? 'bg-[#0a1628] py-3 shadow-lg'
            : 'bg-transparent py-4 md:py-6'
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center group"
              data-testid="navbar-logo"
            >
              <motion.div 
                className="relative"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src="/varyaz-logo-full.png" 
                  alt="Varyaz Trade" 
                  className="h-10 sm:h-12 w-auto object-contain"
                />
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                  className="relative px-5 xl:px-6 py-3 group"
                  onMouseEnter={() => setHoveredLink(link.path)}
                  onMouseLeave={() => setHoveredLink(null)}
                >
                  <span className={`text-sm font-medium tracking-wide transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-secondary-400'
                      : 'text-white/70 group-hover:text-white'
                  }`}>
                    {link.label}
                  </span>
                  
                  {/* Animated underline */}
                  <motion.span
                    className="absolute bottom-2 left-5 xl:left-6 right-5 xl:right-6 h-px bg-secondary-500"
                    initial={{ scaleX: 0 }}
                    animate={{ 
                      scaleX: location.pathname === link.path || hoveredLink === link.path ? 1 : 0 
                    }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    style={{ originX: 0 }}
                  />
                </Link>
              ))}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Language Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 text-sm text-white/60 hover:text-white transition-colors"
                    data-testid="language-switcher"
                  >
                    <Globe className="w-4 h-4" />
                    <span className="font-mono text-xs uppercase tracking-wider hidden sm:inline">{currentLang?.code}</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align={direction === 'rtl' ? 'start' : 'end'} 
                  className="w-48 bg-[#0a0f0d] border-white/10 mt-2"
                >
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      data-testid={`lang-${lang.code}`}
                      className={`flex items-center gap-3 cursor-pointer py-3 text-white/70 hover:text-white hover:bg-white/5 ${
                        currentLanguage === lang.code ? 'bg-white/10 text-secondary-400' : ''
                      }`}
                    >
                      <img src={lang.flag} alt={lang.name} className="w-6 h-4 object-cover rounded-sm" />
                      <span className="text-sm">{lang.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* CTA Button - Desktop */}
              <Link to="/rfq" className="hidden lg:block">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="group relative overflow-hidden bg-secondary-500 px-6 xl:px-8 py-3 xl:py-4 text-xs font-bold uppercase tracking-[0.15em] xl:tracking-[0.2em] text-primary-900 transition-all duration-300"
                  data-testid="nav-rfq-btn"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    {t('nav.rfq')}
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                  <motion.div
                    className="absolute inset-0 bg-white"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                </motion.button>
              </Link>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-white"
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu - Full Screen Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#0a1628] lg:hidden overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="min-h-screen flex flex-col justify-center px-6 sm:px-8 py-20">
              {/* Navigation Links */}
              <nav className="space-y-1">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                      className={`flex items-center justify-between py-4 border-b border-white/10 text-2xl sm:text-3xl font-heading font-bold transition-colors ${
                        location.pathname === link.path 
                          ? 'text-secondary-500' 
                          : 'text-white hover:text-secondary-400'
                      }`}
                    >
                      <span>{link.label}</span>
                      <ArrowUpRight className={`w-5 h-5 transition-colors ${
                        location.pathname === link.path ? 'text-secondary-500' : 'text-white/30'
                      }`} />
                    </Link>
                  </motion.div>
                ))}
              </nav>

              {/* Mobile Language Switcher */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-10 pt-8 border-t border-white/10"
              >
                <p className="text-xs font-mono uppercase tracking-widest text-white/40 mb-4">
                  Language
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all ${
                        currentLanguage === lang.code
                          ? 'bg-secondary-500 text-primary-900'
                          : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <img src={lang.flag} alt={lang.name} className="w-5 h-3.5 object-cover rounded-sm" />
                      <span className="font-mono text-xs uppercase">{lang.code}</span>
                    </button>
                  ))}
                </div>
              </motion.div>

              {/* Mobile CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8"
              >
                <Link
                  to="/rfq"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-nav-rfq"
                >
                  <button className="w-full bg-secondary-500 text-primary-900 py-5 text-sm font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-2">
                    {t('nav.rfq')}
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </Link>
              </motion.div>

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-auto pt-10 text-center"
              >
                <p className="text-white/40 text-sm">info@varyaztrade.com</p>
                <p className="text-white/40 text-sm">+90 537 702 09 27</p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
